import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";
import { TITLES, COUNTRIES, DIAL_CODES, isValidEmail } from "../lib/formData";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const inputCls =
  "w-full bg-rr-bg/40 border border-rr-line rounded-lg px-4 py-3 text-rr-ivory placeholder-rr-ivory/40 text-sm focus:border-rr-gold outline-none transition-colors";

export const PartnerSection = () => {
  const { t } = useTranslation();
  const f = t("partner.form", { returnObjects: true });
  const interests = t("partner.interests", { returnObjects: true });
  const [form, setForm] = useState({ title: "Mr", name: "", email: "", company: "", country: "", dial: "+44", phone: "", inquiry_type: interests[0], message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Please add your name"); return; }
    if (!isValidEmail(form.email)) { setEmailErr("Please enter a valid email address"); return; }
    setEmailErr("");
    setSending(true);
    try {
      await api.post("/leads", {
        name: `${form.title} ${form.name}`.trim(),
        email: form.email,
        country: form.country,
        phone: form.phone ? `${form.dial} ${form.phone}` : "",
        company: form.company,
        inquiry_type: form.inquiry_type,
        message: form.message,
        preferred_retreat: "Investor / Partner enquiry",
        locale: (navigator.language || "").slice(0, 2),
      });
      setSent(true);
    } catch { toast.error("Could not send — please try again"); }
    finally { setSending(false); }
  };

  return (
    <section id="partners" data-testid="partner-section" className="relative border-t border-rr-line bg-rr-obsidian">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-14 md:gap-20 items-start">
          <div>
            <div className="hairline mb-6" />
            <span className="overline">{t("partner.overline")}</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-rr-ivory leading-[1.05]">{t("partner.title")}</h2>
            <p className="text-rr-ivory/65 text-base mt-6 leading-relaxed max-w-md">{t("partner.sub")}</p>
            <p className="text-rr-ivory/40 text-xs mt-8 tracking-wide" data-testid="partner-reg">{t("partner.reg")}</p>
          </div>

          {sent ? (
            <div data-testid="partner-success" className="rounded-2xl border border-rr-gold/30 bg-rr-bg/40 p-10 flex flex-col items-start">
              <CheckCircle2 className="w-10 h-10 text-rr-gold" strokeWidth={1.4} />
              <h3 className="font-heading text-3xl text-rr-ivory mt-4">{t("partner.successTitle")}</h3>
              <p className="text-rr-ivory/65 text-sm mt-2 max-w-sm">{t("partner.successBody")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="partner-form" noValidate className="space-y-4">
              <div className="grid grid-cols-[90px_1fr] gap-4">
                <select data-testid="partner-title" value={form.title} onChange={set("title")} className={inputCls}>
                  {TITLES.map((tt) => <option key={tt} value={tt} className="bg-rr-surface text-rr-ivory">{tt}</option>)}
                </select>
                <input data-testid="partner-name" value={form.name} onChange={set("name")} placeholder={f.name} className={inputCls} />
              </div>
              <div>
                <input data-testid="partner-email" type="email" value={form.email} onChange={set("email")} placeholder={f.email} className={inputCls} />
                {emailErr && <p data-testid="partner-email-error" className="text-red-400 text-xs mt-1.5">{emailErr}</p>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="partner-company" value={form.company} onChange={set("company")} placeholder={f.company} className={inputCls} />
                <select data-testid="partner-country" value={form.country} onChange={set("country")} className={inputCls}>
                  <option value="" className="bg-rr-surface text-rr-ivory/50">{f.country}</option>
                  {COUNTRIES.map((c) => <option key={c.n} value={c.n} className="bg-rr-surface text-rr-ivory">{c.n}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-[110px_1fr] gap-4">
                <select data-testid="partner-dial" value={form.dial} onChange={set("dial")} className={inputCls}>
                  {DIAL_CODES.map((d) => <option key={d} value={d} className="bg-rr-surface text-rr-ivory">{d}</option>)}
                </select>
                <input data-testid="partner-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder={f.phone || "Phone"} className={inputCls} />
              </div>
              <select data-testid="partner-interest" value={form.inquiry_type} onChange={set("inquiry_type")} className={inputCls}>
                {interests.map((it) => <option key={it} value={it} className="bg-rr-surface text-rr-ivory">{it}</option>)}
              </select>
              <textarea data-testid="partner-message" value={form.message} onChange={set("message")} placeholder={f.message} rows={4} className={`${inputCls} resize-none`} />
              <button type="submit" disabled={sending} data-testid="partner-submit" className="btn-gold px-9 py-4 flex items-center gap-2 text-xs uppercase">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{f.submit} <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
